import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { CustomTypography } from './CustomTypography';
import { ColorDotPicker } from './ColorDotPicker';
import { DEFAULT_COMPARISON_COLORS } from 'utils/colorOptions';
import { formatDateRange, formatNumberWithCommas, getImagePath, getTotalRow } from 'utils/AppUtil';

const carrierColors = {
    'AT&T': '#00A8E1',
    Juice: '#FF8C00',
    Private: '#FF8C00',
    'T-Mobile': '#E20074',
};

const comparisonGrid = {
    display: 'grid',
    gridTemplateColumns: 'minmax(150px, 1fr) repeat(3, minmax(70px, 1fr)) repeat(2, minmax(180px, 1.4fr)) repeat(2, minmax(85px, 1fr))',
    minWidth: '890px',
    alignItems: 'stretch',
};

const metrics = [
    { key: 'userCount', label: 'Users', span: 3 },
    { key: 'download', label: 'Download', unit: 'GB', span: 1 },
    { key: 'upload', label: 'Upload', unit: 'GB', span: 1 },
    { key: 'volte', label: 'Calls', span: 2 },
];

const formatValue = (value, key) => {
    if (value === null || value === undefined || value === '') return '--';
    return key === 'userCount' || key === 'volte'
        ? formatNumberWithCommas(value)
        : value;
};

const formatMetricValue = (value, metric) => {
    if (!hasValue(value)) return { text: '--', unit: null };

    if (metric.key === 'download' || metric.key === 'upload') {
        const gigabytes = Number(String(value).replace(/,/g, ''));
        if (Number.isFinite(gigabytes) && Math.abs(gigabytes) >= 1000) {
            return { text: (gigabytes / 1024).toFixed(2), unit: 'TB' };
        }
        return { text: value, unit: 'GB' };
    }

    return { text: formatValue(value, metric.key), unit: null };
};

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const getTrend = (firstValue, secondValue) => {
    if (!hasValue(firstValue) || !hasValue(secondValue)) return null;

    const firstNumber = Number(String(firstValue).replace(/,/g, ''));
    const secondNumber = Number(String(secondValue).replace(/,/g, ''));
    if (!Number.isFinite(firstNumber) || !Number.isFinite(secondNumber)) return null;
    if (secondNumber > firstNumber) return 'up';
    if (secondNumber < firstNumber) return 'down';
    return 'same';
};

const TrendIcon = ({ firstValue, secondValue }) => {
    const trend = getTrend(firstValue, secondValue);

    if (trend === 'up') {
        return <ArrowUpwardIcon titleAccess="Increased" sx={{ ml: 0.25, fontSize: 18, color: '#2E7D32' }} />;
    }
    if (trend === 'down') {
        return <ArrowDownwardIcon titleAccess="Decreased" sx={{ ml: 0.25, fontSize: 18, color: '#C62828' }} />;
    }
    if (trend === 'same') {
        return <ArrowForwardIcon titleAccess="No change" sx={{ ml: 0.25, fontSize: 18, color: '#6B778C' }} />;
    }
    return null;
};

const MetricComparison = ({ firstRow, secondRow, metric, comparisonColors }) => {
    const firstValue = formatMetricValue(firstRow?.[metric.key], metric);
    const secondValue = formatMetricValue(secondRow?.[metric.key], metric);

    return (
        <Stack
            justifyContent="center"
            sx={{
                gridColumn: `span ${metric.span}`,
                minWidth: 0,
                px: 1.25,
                py: 1.5,
                borderLeft: '1px solid #d8dee6',
                overflow: 'hidden',
            }}
        >
            <Stack
                direction="row"
                alignItems="baseline"
                sx={{ minWidth: 0, maxWidth: '100%', gap: 0.6, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
            >
                <Typography component="span" sx={{ fontSize: '16px', fontWeight: 700, color: comparisonColors.first }}>
                    {firstValue.text}
                    {firstValue.unit && (
                        <Box component="span" sx={{ ml: 0.35, fontSize: '10px', fontWeight: 500 }}>{firstValue.unit}</Box>
                    )}
                </Typography>
                <Typography component="span" aria-hidden="true" sx={{ fontSize: '14px', fontWeight: 500, color: '#7A869A' }}>
                    /
                </Typography>
                <Typography component="span" sx={{ fontSize: '16px', fontWeight: 700, color: comparisonColors.second }}>
                    {secondValue.text}
                    {secondValue.unit && (
                        <Box component="span" sx={{ ml: 0.35, fontSize: '10px', fontWeight: 500 }}>{secondValue.unit}</Box>
                    )}
                </Typography>
                <TrendIcon firstValue={firstRow?.[metric.key]} secondValue={secondRow?.[metric.key]} />
            </Stack>
            <Typography sx={{ mt: 0.25, fontSize: '11px', color: '#7A869A' }}>{metric.label}</Typography>
        </Stack>
    );
};

export const ComparisonCarrierTable = ({
    universityName,
    firstData = [],
    secondData = [],
    firstDates,
    secondDates,
    firstVolteCallsCount,
    secondVolteCallsCount,
    isLoading,
    hasError,
    comparisonColors = DEFAULT_COMPARISON_COLORS,
    onComparisonColorChange,
}) => {
    if (isLoading) {
        return (
            <Box sx={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={36} aria-label="Loading comparison data" />
            </Box>
        );
    }

    if (hasError) {
        return <Alert severity="error">Unable to load the comparison range.</Alert>;
    }

    const firstByCarrier = new Map(firstData.map((row) => [row.name, row]));
    const secondByCarrier = new Map(secondData.map((row) => [row.name, row]));
    const carrierNames = [...new Set([...firstByCarrier.keys(), ...secondByCarrier.keys()])];
    const firstTotals = { ...getTotalRow(firstData), volte: firstVolteCallsCount };
    const secondTotals = { ...getTotalRow(secondData), volte: secondVolteCallsCount };

    const renderValues = (firstRow, secondRow) => metrics.map((metric) => (
        <MetricComparison
            key={metric.key}
            firstRow={firstRow}
            secondRow={secondRow}
            metric={metric}
            comparisonColors={comparisonColors}
        />
    ));

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                spacing={3}
                aria-label="Comparison date legend"
                sx={{ mb: 2, px: 1.5, py: 1.25, backgroundColor: '#f7f9fb', border: '1px solid #e1e6ec', borderRadius: '4px' }}
            >
                {[
                    { key: 'first', dates: firstDates, color: comparisonColors.first },
                    { key: 'second', dates: secondDates, color: comparisonColors.second },
                ].map((item) => (
                    <Stack key={item.key} direction="row" alignItems="center" spacing={1}>
                        <ColorDotPicker
                            color={item.color}
                            onChange={(hex) => onComparisonColorChange?.(item.key, hex)}
                            disabledColor={item.key === 'first' ? comparisonColors.second : comparisonColors.first}
                        />
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: item.color }}>
                            {formatDateRange(item.dates)}
                        </Typography>
                    </Stack>
                ))}
            </Stack>

            <Box sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <Box sx={{ ...comparisonGrid, mb: 1, backgroundColor: '#0A2240', color: 'white', borderRadius: '4px' }}>
                    <Stack justifyContent="center" sx={{ px: 1.5, py: 1.25 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Carrier</Typography>
                    </Stack>
                    <Stack justifyContent="center" sx={{ gridColumn: 'span 3', px: 1.5, py: 1, borderLeft: '1px solid rgba(255,255,255,0.35)' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>User Count</Typography>
                        <Typography sx={{ mt: 0.5, fontSize: '11px', color: '#d9e3ed' }}>Average per day</Typography>
                    </Stack>
                    <Stack justifyContent="center" sx={{ gridColumn: 'span 2', px: 1.5, py: 1, borderLeft: '1px solid rgba(255,255,255,0.35)' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Data Usage</Typography>
                        <Typography sx={{ mt: 0.5, fontSize: '11px', color: '#d9e3ed' }}>Total</Typography>
                    </Stack>
                    <Stack justifyContent="center" sx={{ gridColumn: 'span 2', px: 1.5, py: 1, borderLeft: '1px solid rgba(255,255,255,0.35)' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>VoLTE</Typography>
                        <Typography sx={{ mt: 0.5, fontSize: '11px', color: '#d9e3ed' }}>Total</Typography>
                    </Stack>
                </Box>

                {carrierNames.map((carrierName) => (
                    <Box
                        key={carrierName}
                        sx={{
                            ...comparisonGrid,
                            mb: 1,
                            backgroundColor: 'white',
                            border: '1px solid #dfe3e8',
                            borderLeft: `4px solid ${carrierColors[carrierName] || '#0A2240'}`,
                            borderRadius: '4px',
                            overflow: 'hidden',
                        }}
                    >
                        <Stack justifyContent="center" sx={{ px: 1.5, py: 1.25 }}>
                            <Box
                                component="img"
                                src={getImagePath(carrierName, universityName)}
                                alt={carrierName}
                                sx={{ maxWidth: '7rem', maxHeight: '2.25rem', objectFit: 'contain', objectPosition: 'left center' }}
                            />
                        </Stack>
                        {renderValues(firstByCarrier.get(carrierName), secondByCarrier.get(carrierName))}
                    </Box>
                ))}

                <Box sx={{ ...comparisonGrid, backgroundColor: '#eef1f4', borderTop: '4px solid #0A2240', borderRadius: '0 0 4px 4px', overflow: 'hidden' }}>
                    <Stack justifyContent="center" sx={{ px: 1.5, py: 1.5 }}>
                        <CustomTypography text="TOTAL" sx={{ fontWeight: 700, fontSize: '14px' }} />
                    </Stack>
                    {renderValues(firstTotals, secondTotals)}
                </Box>
            </Box>
        </Box>
    );
};